// Mock data for Fountain of Youth Extra Virgin Olive Oil

export const BRAND = {
  name: "Fountain of Youth",
  full: "Fountain of Youth Extra Virgin Olive Oil",
  tagline: "The world's most polyphenol-rich extra virgin olive oil.",
};

export const NAV_LINKS = [
  { label: "The Edge", href: "/the-edge" },
  { label: "Availability & Waitlist", href: "/waitlist" },
  { label: "Health Benefits", href: "/health-benefits" },
  { label: "About Us", href: "/about" },
  { label: "2025 Analysis", href: "/analysis" },
  { label: "FAQ", href: "/faq" },
  { label: "Press & Media", href: "/press" },
  { label: "Resources", href: "/resources" },
];

export const HERO = {
  title: "Drink From the Fountain of Youth",
  subtitle: "The World's Most Polyphenol-Rich Extra Virgin Olive Oil",
  bullets: [
    "Record-breaking polyphenol concentration, sealed at peak potency in zero-oxidation pods.",
    "Independently verified using IOC-accredited LC-MS/MS methodology.",
    "Grown through regenerative permaculture and refined by analytical science.",
  ],
  harvest: [
    "11,636 mg/kg total bioactive compounds",
    "2,236 mg/kg total polyphenols",
    "Up to 45x more than supermarket olive oil",
    "University-laboratory verified",
  ],
};

export const BENEFITS_SHORT = [
  "Supports heart & brain function",
  "Boosts gut microbiome diversity",
  "Powerful natural anti-inflammatory",
  "Neutralises free radicals & oxidative stress",
  "Defends against neurodegeneration",
];

export const PROCESS_STEPS = [
  {
    title: "Early-Harvest Picking",
    desc: "Olives are harvested green, weeks before commercial maturity, when phenolic concentration is at its absolute peak.",
    icon: "Sprout",
  },
  {
    title: "In-Grove Cold Storage",
    desc: "Mobile refrigeration units cool the fruit within minutes of picking, preserving the volatile compounds that define potency.",
    icon: "Snowflake",
  },
  {
    title: "Low-Temperature Extraction",
    desc: "A short malaxation cycle under nitrogen blanket keeps oxygen out and locks every phenolic molecule into the oil.",
    icon: "FlaskConical",
  },
  {
    title: "Independent Lab Verification",
    desc: "Every harvest is tested by two independent laboratories using LC-MS/MS, the analytical gold standard.",
    icon: "Microscope",
  },
  {
    title: "Carbon-Negative Cultivation",
    desc: "Our regenerative groves sequester 16 kg of CO\u2082 per litre produced \u2014 verified by the IOC CarbonBalance methodology.",
    icon: "Leaf",
  },
];

export const PRODUCTS = [
  {
    id: "1m",
    name: "1 Month Supply",
    pods: 30,
    price: 89,
    perDay: 2.97,
    badge: null,
    desc: "30 nitrogen-sealed 5ml pods. A perfect month-long introduction to the daily ritual.",
  },
  {
    id: "3m",
    name: "3 Months Supply",
    pods: 90,
    price: 249,
    perDay: 2.77,
    badge: "Most Popular",
    desc: "90 nitrogen-sealed pods. The minimum window most research uses to measure meaningful change.",
  },
  {
    id: "6m",
    name: "6 Months Supply",
    pods: 180,
    price: 479,
    perDay: 2.66,
    badge: "Best Value",
    desc: "180 nitrogen-sealed pods. Six months of clinically-relevant dosing at our best per-pod price.",
  },
  {
    id: "12m",
    name: "12 Months Supply",
    pods: 360,
    price: 899,
    perDay: 2.5,
    badge: "Annual",
    desc: "A full year of premium polyphenol nutrition. Locked-in pricing across the entire harvest.",
  },
];

export const HEALTH_BENEFITS = [
  {
    category: "Cardiovascular Health",
    stat: "30%",
    headline: "fewer major cardiovascular events",
    body:
      "The PREDIMED study (7,447 participants, published in the New England Journal of Medicine) found that a Mediterranean diet supplemented with extra-virgin olive oil reduced heart attacks, strokes, and cardiovascular death by around 30%. A sub-study of the EUROLIVE trial showed high-polyphenol oils also lowered small-dense LDL particles by 15.3% and improved HDL functionality.",
    cite: "Estruch et al., 2018, New England Journal of Medicine",
    icon: "Heart",
  },
  {
    category: "Inflammation",
    stat: "Ibuprofen-like",
    headline: "Oleocanthal inhibits the same enzymes",
    body:
      "In 2005, researchers identified the peppery throat-sting of fresh olive oil as the molecule oleocanthal \u2014 a natural COX-1 and COX-2 inhibitor with the same molecular target as ibuprofen. Daily consumption may help explain why Mediterranean populations show consistently lower rates of inflammation-driven disease.",
    cite: "Beauchamp et al., 2005, Nature (437:45-46)",
    icon: "Flame",
  },
  {
    category: "Blood Sugar",
    stat: "30%",
    headline: "fewer new diabetes cases \u2014 no calorie restriction",
    body:
      "Following 3,541 participants for over four years, the PREDIMED trial saw the EVOO group develop around 30% fewer new diabetes cases than the low-fat control. A 2025 follow-up confirmed HbA1c improvements in just 100 days of daily polyphenol-rich olive oil intake.",
    cite: "Salas-Salvad\u00f3 et al., 2014, Annals of Internal Medicine",
    icon: "Droplet",
  },
  {
    category: "Brain Health & Neuroprotection",
    stat: "28%",
    headline: "lower risk of dementia-related death over 28 years",
    body:
      "A Harvard cohort tracking 92,383 adults found that consuming more than half a tablespoon of olive oil daily was associated with a 28% lower risk of dementia mortality \u2014 regardless of overall diet quality or APOE \u03b54 genetic risk. Oleocanthal also supports the brain's clearance of beta-amyloid plaque.",
    cite: "Tessier et al., 2024, JAMA Network Open",
    icon: "Brain",
  },
  {
    category: "Gut Health & Microbiome",
    stat: "90\u201395%",
    headline: "of polyphenols reach the colon intact",
    body:
      "Olive polyphenols largely bypass digestion in the stomach and small intestine. They reach the colon where they act as a natural prebiotic \u2014 promoting Bifidobacterium and Lactobacillus while suppressing pathogens. A 2025 human trial confirmed gut composition shifts and reduced IL-1\u03b2.",
    cite: "Nutrients, 2020 \u2022 Biomolecules, 2025",
    icon: "Wheat",
  },
  {
    category: "Skin Protection",
    stat: "Top-tier",
    headline: "antioxidant capacity among natural compounds",
    body:
      "Hydroxytyrosol, the most potent antioxidant in olive oil, protects skin cells from UVB-induced damage and inhibits the enzymes that break down collagen. Regular intake builds systemic antioxidant capacity from the inside out.",
    cite: "D'Angelo et al., 2014, Int. J. Molecular Sciences",
    icon: "Sun",
  },
  {
    category: "Bone & Joint Health",
    stat: "+30%",
    headline: "increase in osteocalcin, the bone-building protein",
    body:
      "In postmenopausal women consuming polyphenol-rich olive oil over 12 months, researchers observed a 30% increase in osteocalcin \u2014 a critical bone formation marker. Oleocanthal also supports joint comfort without the GI risks of long-term NSAID use.",
    cite: "Filip et al., 2015, J. Nutrition, Health and Aging",
    icon: "Bone",
  },
  {
    category: "Anticancer Properties",
    stat: "30 min.",
    headline: "Selectively destroys cancer cells in laboratory studies",
    body:
      "Researchers at Hunter College and Rutgers showed oleocanthal ruptured the fragile lysosomes of breast, prostate, and pancreatic cancer cells \u2014 triggering rapid cell death. Healthy cells exposed to identical doses simply paused growth temporarily.",
    cite: "LeGendre, Breslin & Foster, 2015, Molecular & Cellular Oncology",
    icon: "ShieldCheck",
  },
  {
    category: "Women's Health",
    stat: "6.6 \u2192 1.1",
    headline: "menstrual pain score reduction (vs 3.8 with ibuprofen)",
    body:
      "In a clinical trial of 60 women with primary dysmenorrhoea, daily 25 ml of olive oil for two months dropped pain scores from 6.6 to 1.1 on a 10-point scale. Additional evidence supports benefits across menopause and PCOS.",
    cite: "Akhlaghi et al., 2015",
    icon: "Flower2",
  },
  {
    category: "Oral Health",
    stat: "58%",
    headline: "reduction in cavity-causing bacteria",
    body:
      "Unlike broad-spectrum mouthwashes, olive polyphenols selectively suppress harmful oral bacteria while preserving beneficial microbes. S. mutans was reduced by 58%, P. gingivalis by 43%, and inflammation markers dropped up to 40%.",
    cite: "Frontiers in Oral Health, 2023",
    icon: "Smile",
  },
];

export const VALUES = [
  { title: "Carbon Negative", icon: "Leaf", desc: "16 kg of CO\u2082 sequestered per litre produced." },
  { title: "Pesticide & Microplastic Free", icon: "ShieldCheck", desc: "Zero synthetic inputs. Verified clean every batch." },
  { title: "Vegan & Cruelty Free", icon: "Heart", desc: "Plant-only operations with a zero-kill grove policy." },
  { title: "Regenerative Agriculture", icon: "Sprout", desc: "Living soil, biodiverse polyculture, integrated animal systems." },
  { title: "Traceability & Transparency", icon: "Eye", desc: "Lab reports, harvest dates, and grove origins on every bottle." },
  { title: "Community First", icon: "Users", desc: "Long-term partnerships with our growers and local researchers." },
];

export const FAQS = [
  {
    q: "What makes Fountain of Youth different from regular olive oil?",
    a: "Most supermarket extra-virgin olive oils contain 50\u2013150 mg/kg of polyphenols. Fountain of Youth is verified at 2,236 mg/kg \u2014 up to 45x higher. Every harvest is independently tested using LC-MS/MS at IOC-accredited laboratories, the analytical method recognised by the EFSA and the International Olive Council.",
  },
  {
    q: "Why monodose pods instead of a bottle?",
    a: "Polyphenols degrade with oxygen, light, heat, and time. Our nitrogen-flushed 5 ml pods preserve full potency for 18+ months. A bottle of even excellent oil can lose half its polyphenol load within weeks of being opened.",
  },
  {
    q: "How do I take it?",
    a: "One 5 ml pod per day \u2014 straight, on bread, or stirred into a finished dish. It is a daily ritual, not a cooking oil. Avoid heating above 70\u00b0C so the phenolic compounds remain intact.",
  },
  {
    q: "Is the taste strong?",
    a: "Yes. Genuine high-phenolic oil has a distinct peppery throat-tingle (the oleocanthal) and a green, bitter character. That sting is the active anti-inflammatory compound \u2014 think of it as the flavour of efficacy.",
  },
  {
    q: "Where is Fountain of Youth produced?",
    a: "Our flagship groves are in the Mediterranean, cultivated through regenerative permaculture: biodiverse polyculture, zero pesticides, and a strict zero-kill policy across the entire estate.",
  },
  {
    q: "Is there a money-back guarantee?",
    a: "Yes. We offer a 30-day satisfaction guarantee on first-time orders. If the oil is not for you, contact our team for a full refund \u2014 no questions asked.",
  },
  {
    q: "Do you ship internationally?",
    a: "We ship across the EU, UK, US and most international destinations through carbon-negative logistics partners.",
  },
  {
    q: "What is the EFSA health claim?",
    a: "Under EU Regulation 432/2012, olive oils containing at least 250 mg/kg of hydroxytyrosol and derivatives may carry the claim that olive oil polyphenols contribute to the protection of blood lipids from oxidative stress. Fountain of Youth exceeds this threshold by nearly nine-fold.",
  },
];

export const ATHLETIC_BENEFITS = [
  { title: "Faster Recovery", desc: "Polyphenols help dampen post-exercise inflammation, shortening the return to full output.", icon: "Zap" },
  { title: "Endurance Support", desc: "Hydroxytyrosol supports mitochondrial function \u2014 the cellular engine of sustained aerobic capacity.", icon: "Activity" },
  { title: "Joint Resilience", desc: "Oleocanthal's COX-inhibiting action eases joint load without the GI risks of long-term NSAIDs.", icon: "Bone" },
  { title: "Oxidative Defence", desc: "Heavy training generates free radicals. Olive polyphenols neutralise them at the cellular level.", icon: "ShieldCheck" },
];

// Spotlight: real, publicly-documented references only. No fabricated endorsements.
export const SPOTLIGHT_REFERENCES = [
  {
    name: "Bryan Johnson",
    role: "Founder, Project Blueprint",
    context: "Longevity research \u2022 publicly documented protocol",
    summary:
      "Has publicly made high-polyphenol extra virgin olive oil a core daily input of his Blueprint longevity protocol, taking it daily and producing his own high-phenolic oil under the Blueprint label.",
    sourceLabel: "blueprint.bryanjohnson.com",
    sourceUrl: "https://blueprint.bryanjohnson.com",
  },
  {
    name: "Kilian Jornet",
    role: "Ultra-endurance athlete & mountaineer",
    context: "Trail running \u2022 multiple Skyrunner World Series & UTMB titles",
    summary:
      "Has spoken publicly in interviews about using olive oil as part of his fuelling strategy during ultra-endurance efforts that demand 9,000+ calories per day, citing its calorie density and quality of fat.",
    sourceLabel: "Public interviews",
    sourceUrl: "https://thefeed.com/insider/why-every-pro-athlete-takes-olive-oil",
  },
  {
    name: "The PREDIMED Trial",
    role: "7,447 participants \u2022 New England Journal of Medicine",
    context: "Landmark Mediterranean-diet study",
    summary:
      "Participants supplementing the Mediterranean diet with extra virgin olive oil saw an approximate 30% reduction in major cardiovascular events. The strongest clinical signal yet for daily olive oil as a long-term performance and longevity input.",
    sourceLabel: "Estruch et al., NEJM 2018",
    sourceUrl: "https://www.nejm.org/doi/full/10.1056/NEJMoa1800389",
  },
  {
    name: "EFSA Health Claim 432/2012",
    role: "European Food Safety Authority",
    context: "Official EU health claim",
    summary:
      "Olive oil polyphenols are the only food compound to carry an EU-authorised health claim for protecting blood lipids from oxidative stress \u2014 the analytical threshold begins at 250 mg/kg of hydroxytyrosol and derivatives. FOYEVOO delivers 892% above that minimum.",
    sourceLabel: "EU Regulation 432/2012",
    sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/ALL/?uri=CELEX:32012R0432",
  },
];

export const EDGE_PROTOCOLS = [
  {
    phase: "Pre-Season",
    desc: "Daily 5ml pod, taken with breakfast. Builds antioxidant capacity and reduces baseline inflammation before training load ramps.",
  },
  {
    phase: "In-Season",
    desc: "Daily 5ml pod, ideally 30 minutes pre-session or with the post-training meal. Supports session-to-session recovery.",
  },
  {
    phase: "Competition Week",
    desc: "Maintain daily dose. Avoid heating above 70\u00b0C to keep phenolic compounds intact.",
  },
  {
    phase: "Off-Season / Travel",
    desc: "Continue daily. Nitrogen-sealed pods travel anywhere \u2014 18+ month stability in a kit bag.",
  },
];

export const EDGE_STATS = [
  { value: "30%", label: "lower inflammation markers in athletic populations (clinical evidence)" },
  { value: "+VO2", label: "Mitochondrial support for sustained aerobic output" },
  { value: "0", label: "Banned substances. WADA-clean, third-party verified" },
  { value: "5 ml", label: "Daily ritual. Pre-session or post-recovery meal" },
];

export const ANALYSIS_DATA = {
  harvest: "2025",
  labs: [
    "Universidad de Cordoba, Department of Analytical Chemistry \u2014 Interreg Mediterranean ARISTOIL project",
    "Chem Solutions Analytical Laboratories, Athens \u2014 IOC-recognised accreditation",
  ],
  metrics: [
    { label: "Total Polyphenols", value: "2,236", unit: "mg/kg" },
    { label: "Total Bioactive Compounds", value: "11,636", unit: "mg/kg" },
    { label: "Hydroxytyrosol & Derivatives", value: "892%", unit: "above EFSA minimum" },
    { label: "Oleocanthal", value: "High", unit: "anti-inflammatory marker" },
    { label: "Oleacein", value: "High", unit: "cardiovascular marker" },
    { label: "Acidity (free fatty acids)", value: "0.18", unit: "% (max 0.8)" },
    { label: "Peroxide Value", value: "5.2", unit: "meqO2/kg" },
    { label: "K232 / K270", value: "1.78 / 0.12", unit: "UV spectrophotometric" },
  ],
};
